import React from 'react';
import { MDText } from 'i18n-react';

import { IconText, InfoIcon, WarningIcon } from '@motorway/mw-highway-code';

import Tooltip from 'Components/shared/Tooltip/Tooltip';
import { triggerDeliveryCostTooltipHoverEvent } from 'Services/analytics/events';
import { applySessionReplayActionName, applySessionReplayMask } from 'Services/datadog/datadogRum';
import { MASKED_EVENT_VALUES } from 'Services/datadog/datadogRum.const';
import type { TransportValue } from 'Types/transportOptions';

import LocalTexts from '../DeliveryLocation/DeliveryLocation.texts';

import styles from './DeliveryPrice.module.scss';

const LocalT = new MDText(LocalTexts);

interface DeliveryPriceLabelProps {
	values: {
		labelText: React.ReactNode;
		requiresFerry: boolean;
	};
}

const { delivery: ddDeliveryValues } = MASKED_EVENT_VALUES;
const privacyParams = {
	deliveryPriceLabel: applySessionReplayMask({ maskedActionName: ddDeliveryValues.price.toLabel }),
	deliveryPriceTooltip: applySessionReplayActionName(ddDeliveryValues.price.tooltip),
	deliveryPriceValue: applySessionReplayMask({ maskedActionName: ddDeliveryValues.price.value }),
};

export const DeliveryPriceLabel: React.FC<DeliveryPriceLabelProps> = ({ values: { labelText, requiresFerry } }) => (
	<div className={styles.warning} {...privacyParams.deliveryPriceLabel}>
		{labelText}
		{requiresFerry && (
			<>
				<br />
				<IconText icon={WarningIcon} text={LocalT.translate('delivery.ferryWarning')} variant="xsmall--small" />
			</>
		)}
	</div>
);

interface DeliveryPriceValueProps {
	delivery: {
		content: string;
		drivingDistance: Nullable<number>;
		requiresFerry: boolean;
		transportValue: TransportValue;
	};
}

export const DeliveryPriceValue: React.FC<DeliveryPriceValueProps> = ({ delivery }) => {
	const {
		content,
		drivingDistance,
		requiresFerry,
		transportValue: { price },
	} = delivery;

	const appendWithDrivingDistance = drivingDistance
		? ` and accurate mileage of ${Math.round(drivingDistance)} miles`
		: '';

	return (
		<div className={styles.info}>
			<div className={styles.price} {...privacyParams.deliveryPriceValue}>
				{price && !requiresFerry ? (
					<>
						<span>{`£${price}`}</span>
						<span className={styles.priceEstimateSuffix}>{LocalT.translate('delivery.plusVat')}</span>
					</>
				) : (
					LocalT.translate('delivery.notApplicable')
				)}
			</div>
			{Boolean(price) && (
				<div className={styles.tooltip} {...privacyParams.deliveryPriceTooltip}>
					<Tooltip
						className={styles.tooltipContainer}
						content={LocalT.translate(content, { appendWithDrivingDistance })}
						contentClassName={styles.tooltipContent}
						id="deliveryPriceTooltip"
						placement="bottom"
						triggerProps={{
							className: styles.tooltipTrigger,
							onMouseEnter: () => triggerDeliveryCostTooltipHoverEvent(),
						}}
					>
						<InfoIcon size="xsmall" />
					</Tooltip>
				</div>
			)}
		</div>
	);
};
